import React, { Component, ReactNode } from "react";
import { SecondaryButton } from "@/components/StandardButton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { AlertCircle, RefreshCcw, Home, ArrowLeft } from "lucide-react";
import { logger } from "@/utils/logger";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

export class AdminErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    logger.error("Admin page error:", { error, errorInfo });
    this.setState({ errorInfo });
    
    // Log to audit trail for admin errors
    try {
      const errorDetails = {
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        url: window.location.href,
        timestamp: new Date().toISOString(),
      };
      logger.error("Admin error details for audit:", errorDetails);
    } catch {
      // Ignore logging errors
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoBack = () => {
    window.history.back();
  };

  handleGoHome = () => {
    window.location.href = "/admins/dashboard";
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex items-center justify-center min-h-[400px] p-4">
          <Alert variant="destructive" className="max-w-lg">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Something went wrong</AlertTitle>
            <AlertDescription className="mt-2 space-y-4">
              <p>We encountered an error loading this admin page.</p>
              {this.state.error && (
                <div className="p-2 bg-destructive/10 rounded-xl">
                  <p className="text-xs opacity-70 font-mono break-all">
                    {this.state.error.message}
                  </p>
                </div>
              )}
              <div className="flex flex-wrap gap-2 pt-2">
                <SecondaryButton onClick={this.handleRetry} size="sm">
                  <RefreshCcw className="h-4 w-4 mr-2" />
                  Try Again
                </SecondaryButton>
                <Button variant="outline" size="sm" onClick={this.handleGoBack}>
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Go Back
                </Button>
                <Button variant="outline" size="sm" onClick={this.handleGoHome}>
                  <Home className="h-4 w-4 mr-2" />
                  Admin Home
                </Button>
              </div>
              <p className="text-xs text-muted-foreground pt-2">
                This error has been logged. If the issue persists, check System Status for service health.
              </p>
            </AlertDescription>
          </Alert>
        </div>
      );
    }

    return this.props.children;
  }
}
